import { NOTE_CLASS } from "../ui/card";
import type {
  JournalQuestData,
  JournalEntry,
  QuestStatus,
} from "@roll1/shared";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { X, Plus } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { useI18n } from "../../i18n";
import { questDescendantIds } from "../../lib/questChain";

interface QuestFormProps {
  data: JournalQuestData;
  onChange: (partial: Partial<JournalQuestData>) => void;
  focusedFieldRef: React.MutableRefObject<string | null>;
  entries?: JournalEntry[];
  /** This quest's own id — it is never offered as its own «follows from» (Q-5). */
  entryId?: string;
  /**
   * The tick of an objective — its own confirmed command, not part of the
   * text save (GM Studio quests 1). Absent: the box is read-only.
   */
  onSetCompleted?: (objectiveId: string, completed: boolean) => void;
  /** The tick of this objective is on its way to the server. */
  isCompletedPending?: (objectiveId: string) => boolean;
}

const QUEST_STATUSES: QuestStatus[] = [
  "active",
  "completed",
  "failed",
  "hidden",
];

export default function QuestForm({
  data,
  onChange,
  focusedFieldRef,
  entries,
  entryId,
  onSetCompleted,
  isCompletedPending,
}: QuestFormProps) {
  const { t } = useI18n();
  const uid = useId();

  const handleFocus = (field: string) => {
    focusedFieldRef.current = field;
  };
  const handleBlur = () => {
    focusedFieldRef.current = null;
  };

  const npcEntries = entries?.filter((e) => e.type === "npc") ?? [];

  // Q-5: the chain. A quest can follow from any other quest that does not itself come after it.
  const after = entryId !== undefined && entries !== undefined ? questDescendantIds(entries, entryId) : new Set<string>();
  const parentChoices = (entries ?? []).filter((e) => e.type === "quest" && e.id !== entryId && !after.has(e.id));
  const follows = data.followsFrom ?? null;
  const parentQuest = follows !== null ? (entries ?? []).find((e) => e.id === follows.questId && e.type === "quest") : undefined;
  const parentObjectives = parentQuest !== undefined ? ((parentQuest.data as JournalQuestData).objectives ?? []) : [];
  const nativeSelect =
    "h-8 w-full rounded-md border border-input bg-transparent px-2 text-ui text-foreground focus:outline-none focus-visible:border-ring";

  // GK-7: Enter in an objective adds the next one and puts the cursor in it.
  const [focusObjectiveId, setFocusObjectiveId] = useState<string | null>(null);
  useEffect(() => {
    if (focusObjectiveId === null) return;
    const el = document.querySelector<HTMLInputElement>(`[data-objective-input="${focusObjectiveId}"]`);
    if (el === null) return;
    el.focus();
    setFocusObjectiveId(null);
  }, [focusObjectiveId, data.objectives]);
  const addObjective = (): string => {
    // The id is made here: the next save and a tick address the same objective.
    const id = crypto.randomUUID();
    onChange({
      objectives: [...data.objectives, { id, text: "", description: "", completed: false, hidden: false }],
    });
    return id;
  };

  const updateObjective = (
    index: number,
    updates: Partial<JournalQuestData["objectives"][0]>,
  ) => {
    const updated = data.objectives.map((o, i) =>
      i === index ? { ...o, ...updates } : o,
    );
    onChange({ objectives: updated });
  };

  const removeObjective = (index: number) => {
    onChange({ objectives: data.objectives.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.status")}
        </Label>
        <Select
          value={data.status}
          onValueChange={(v) => onChange({ status: v as QuestStatus })}
        >
          <SelectTrigger className="h-8 text-ui">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {QUEST_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {t(`journal.form.questStatus.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.questGiver")}
        </Label>
        <Select
          value={data.questGiverEntryId ?? "__none__"}
          onValueChange={(v) =>
            onChange({ questGiverEntryId: v === "__none__" ? undefined : v })
          }
        >
          <SelectTrigger className="h-8 text-ui">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">{t("common.none")}</SelectItem>
            {npcEntries.map((e) => (
              <SelectItem key={e.id} value={e.id}>
                {e.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label htmlFor={`${uid}-from`} className="text-meta text-muted-foreground">
          {t("journal.form.quest.followsFrom")}
        </label>
        <select
          id={`${uid}-from`}
          className={nativeSelect}
          value={follows?.questId ?? ""}
          onChange={(e) => onChange({ followsFrom: e.target.value === "" ? null : { questId: e.target.value } })}
        >
          <option value="">—</option>
          {parentChoices.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
        {follows !== null && parentQuest !== undefined && (
          <div className="mt-1.5">
            <label htmlFor={`${uid}-after`} className="text-meta text-muted-foreground">
              {t("journal.form.quest.followsObjective")}
            </label>
            <select
              id={`${uid}-after`}
              className={nativeSelect}
              value={follows.objectiveId ?? ""}
              onChange={(e) =>
                onChange({
                  followsFrom: e.target.value === "" ? { questId: follows.questId } : { questId: follows.questId, objectiveId: e.target.value },
                })
              }
            >
              <option value="">{t("journal.form.quest.followsAnyObjective")}</option>
              {parentObjectives.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.text}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.description")}
        </Label>
        <Textarea
          className="text-ui min-h-[80px]"
          value={data.description}
          onChange={(e) => onChange({ description: e.target.value })}
          onFocus={() => handleFocus("description")}
          onBlur={handleBlur}
        />
      </div>
      <div className={NOTE_CLASS}>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.gmNotes")}
          <span className="ml-1 text-warn text-label">
            {t("journal.editor.gmOnly")}
          </span>
        </Label>
        <Textarea
          className="text-ui min-h-[60px]"
          value={data.gmNotes}
          onChange={(e) => onChange({ gmNotes: e.target.value })}
          onFocus={() => handleFocus("gmNotes")}
          onBlur={handleBlur}
        />
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.objectives")}
        </Label>
        {data.objectives.map((obj, i) => (
          <div key={obj.id ?? i} className="mt-1" data-testid="quest-form-objective">
          <div className="flex items-center gap-1">
            <Checkbox
              checked={obj.completed}
              disabled={onSetCompleted === undefined || (isCompletedPending?.(obj.id) ?? false)}
              onCheckedChange={(checked) =>
                onSetCompleted?.(obj.id, checked === true)
              }
              aria-label={t("journal.form.quest.objectiveCompleted")}
            />
            <Input
              className="h-7 text-ui flex-1"
              value={obj.text}
              onChange={(e) => updateObjective(i, { text: e.target.value })}
              data-objective-input={obj.id}
              onKeyDown={(e) => {
                if (e.key !== "Enter" || e.nativeEvent.isComposing || obj.text.trim() === "") return;
                e.preventDefault();
                // On the last row Enter adds the next objective; on an earlier one it moves to the next row.
                if (i === data.objectives.length - 1) setFocusObjectiveId(addObjective());
                else {
                  const next = data.objectives[i + 1]?.id;
                  if (next !== undefined) setFocusObjectiveId(next);
                }
              }}
              onFocus={() => handleFocus(`objectives.${i}`)}
              onBlur={handleBlur}
              placeholder={t("journal.form.quest.objectiveText")}
            />
            <div className="border-l-2 border-hl pl-1 flex items-center">
              <Checkbox
                checked={obj.hidden}
                onCheckedChange={(checked) =>
                  updateObjective(i, { hidden: !!checked })
                }
                aria-label={t("journal.form.quest.objectiveHidden")}
              />
              <span className="text-label text-warn ml-0.5">
                {t("journal.form.quest.objectiveHidden")}
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-7 p-0"
              onClick={() => removeObjective(i)}
            >
              <X size={14} />
            </Button>
          </div>
          <Textarea
            className="ml-5 mt-1 min-h-[44px] w-[calc(100%-1.25rem)] text-meta"
            value={obj.description ?? ""}
            onChange={(e) => updateObjective(i, { description: e.target.value })}
            onFocus={() => handleFocus(`objectives.${i}.description`)}
            onBlur={handleBlur}
            placeholder={t("journal.form.quest.objectiveDescription")}
            aria-label={t("journal.form.quest.objectiveDescription")}
            data-testid="quest-form-objective-description"
          />
          </div>
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="mt-1"
          onClick={() => { addObjective(); }}
        >
          <Plus size={14} className="mr-1" />
          {t("journal.editor.addItem")}
        </Button>
      </div>
      <div>
        <Label className="text-meta text-muted-foreground">
          {t("journal.form.quest.rewards")}
        </Label>
        <Textarea
          className="text-ui min-h-[60px]"
          value={data.rewards ?? ""}
          onChange={(e) => onChange({ rewards: e.target.value })}
          onFocus={() => handleFocus("rewards")}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
}
